import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { conciergeLoginThunk } from 'redux/Reducers/Concierge/conciergeLoginSlicer';
import { getRequestSolicitationEditThunk } from 'redux/Reducers/Concierge/requestSolicitationEditSlicer';
import { resetSupplierCreationData } from 'redux/Reducers/Concierge/createSupplierSlice';
import { resetDataUploadFile } from 'redux/Reducers/Concierge/uploadFileSlicer';
import { resetDataValidateBarCode } from 'redux/Reducers/Concierge/validateBarCodeSlicer';
import { getPointsThunk } from 'redux/Reducers/Concierge/getPointsSlicer';

import 'Assets/styles/default/pages/concierge/NewConciergeSolicitationStyles.less';

import { Col, Row } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { Typography, VertBreadcrumb, VertContainer, VertIcon, VertLoading, VertSteps } from '@vertem-core/vertice-ds';

import ClientInformation from 'components/Concierge/Components/ClientInformation';
import SupplierInformation from 'components/Concierge/Components/SupplierInformation';
import Delivery from 'components/Concierge/Components/Delivery';
import SolicitationInformation from 'components/Concierge/Components/SolicitationInformation';
import ErrorApiModal from 'components/Concierge/Modals/ErrorApiModal';

import { CategoryTypeEnum } from 'pages/ManageConciergeSolicitationPage/enum/action-enum';
import { BaseSteps, RegisterSteps } from 'pages/NewConciergeSolicitationPage/enum/new-solicitation-enum';

import { NewSolicitationInitialState } from 'Constants/Concierge/initialStateValues';
import FinishSolicitation from 'components/Concierge/Components/FinishSolicitation';

const NewConciergeSolicitationPage = () => {
    const { solicitationId } = useParams();

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [currentStep, setCurrentStep] = useState(RegisterSteps.SelectFlow);
    const [registerSteps, setRegisterSteps] = useState([]);
    const [stepItems, setStepItems] = useState([]);
    const [solicitation, setSolicitation] = useState(NewSolicitationInitialState);
    const [showModalErrorLogin, setShowModalErrorLogin] = useState(false);
    const [userPoints, setUserPoints] = useState({ pointsValue: 0 });

    const {
        dataRequestSolicitationEdit,
        loadingRequestSolicitationEdit,
        errorRequestSolicitationEdit
    } = useSelector(
        (state) => state.RequestSolicitationEdit
    );

    const {
        dataConciergeLogin,
        errorConciergeLogin,
    } = useSelector(
        (state) => state.ConciergeLogin
    );

    const {
        dataGetPoints
    } = useSelector(
        (state) => state.GetPoints
    );

    const currentSequence = stepItems.find(item => item.step == currentStep)?.sequence || 0;

    const handleStep = (currentStep) => {
        switch (currentStep) {
            case RegisterSteps.SelectFlow:
                return <ClientInformation
                    userPoints={userPoints}
                    setUserPoints={setUserPoints}
                    solicitation={solicitation}
                    setSolicitation={setSolicitation}
                    handleNextStep={handleNextStep}
                    setFlowSteps={setRegisterSteps} />

            case RegisterSteps.SelectProduct:
                return <SolicitationInformation
                    userPoints={userPoints}
                    solicitation={solicitation}
                    setSolicitation={setSolicitation}
                    handleNextStep={handleNextStep}
                    handlePreviousStep={handlePreviousStep} />

            case RegisterSteps.SelectSupplier:
                return <SupplierInformation
                    solicitation={solicitation}
                    setSolicitation={setSolicitation}
                    handleNextStep={handleNextStep}
                    handlePreviousStep={handlePreviousStep} />

            case RegisterSteps.SelectAddress:
                return <Delivery
                    solicitation={solicitation}
                    setSolicitation={setSolicitation}
                    handleNextStep={handleNextStep}
                    handlePreviousStep={handlePreviousStep} />

            case RegisterSteps.Finish:
                return <FinishSolicitation
                    solicitation={solicitation}
                    flowSteps={registerSteps}
                    handlePreviousStep={handlePreviousStep} />

            default:
                return;
        }
    }

    const setStepSequence = (sequence) => {
        const item = stepItems.find(item => item.sequence == sequence);
        setCurrentStep(item.step);
    }

    useEffect(() => {
        if (dataConciergeLogin) {
            if (solicitationId != undefined) {
                dispatch(getRequestSolicitationEditThunk(solicitationId));
            }
        }
    }, [dataConciergeLogin]);

    useEffect(() => {
        if (!dataRequestSolicitationEdit || !solicitationId) return;

        const { redemptionWorkflow, details, solicitationItemsDetails, addresses } = dataRequestSolicitationEdit;

        setCurrentStep(RegisterSteps.SelectProduct);
        setRegisterSteps(redemptionWorkflow.registerSteps || []);

        const { id: requestTypeId, flowId: requestTypeFlowId, isQuote, isConcierge, isHierarchy, type } = redemptionWorkflow;
        const { participantId, participantId: clientId, participantDocument: clientDocument, file = null } = details;
        const { favoriteSupplierId: supplierId = null, favoriteSupplierName: supplierLabel = null, items } = solicitationItemsDetails;

        const productList = items.map(item => ({
            id: item.id,
            category: item.category,
            categoryLabel: item.categoryLabel,
            subCategory: item.subcategory,
            subCategoryLabel: item.subcategoryLabel,
            title: item.name,
            quantity: item.quantity.toString(),
            unitValue: item.value.toString(),
            pointsValue: item.valuePoints,
            pointsTotalValue: item.totalValuePoints,
            description: item.description,
        }));

        const solicitationOverview = {
            redemptionId: solicitationId,
            requestTypeId,
            requestTypeFlowId,
            type,
            isBillPayment: type == CategoryTypeEnum.BillPayment,
            isQuote,
            isConcierge,
            isHierarchy,
            participantId,
            clientId,
            clientDocument,
            productList,
            ...(supplierId && {
                haveFavoriteSupplier: true,
                supplierId,
                supplierLabel,
            }),
            ...(file && {
                quoteFileUrl: file.url,
                quoteFileName: file.fileName,
                fileList: [file.fileName]
            }),
            billingAddress: addresses.find(addr => addr.billingAddress)?.billingAddress,
            deliveryAddress: addresses.find(addr => addr.deliveryAddress)?.deliveryAddress,
        };

        setSolicitation(solicitationOverview);

    }, [dataRequestSolicitationEdit]);

    useEffect(() => {
        if (errorConciergeLogin) {
            setShowModalErrorLogin(true);
        }
    }, [errorConciergeLogin]);

    useEffect(() => {
        if (dataGetPoints)
            setUserPoints(dataGetPoints);
    }, [dataGetPoints]);

    const handleNextStep = (solicitation) => {
        const actualStep = stepItems.find(step => step.step == currentStep);
        const nextStep = stepItems.find(step => step.sequence == actualStep.sequence + 1);

        setCurrentStep(nextStep.step);
        setSolicitation({ ...solicitation });
    }

    const handlePreviousStep = () => {
        const actualStep = stepItems.find(step => step.step == currentStep);
        const previousStep = stepItems.find(step => step.sequence == actualStep.sequence - 1);

        setCurrentStep(previousStep.step);
    }

    useEffect(() => {
        const hasCustomSteps = registerSteps.length > 0;

        const steps = hasCustomSteps
            ? BaseSteps.filter(step => registerSteps.some(item => step.step == item.step))
            : structuredClone(BaseSteps);

        if (!steps.some(item => item.step == RegisterSteps.Finish)) {
            steps.push(BaseSteps.find(item => item.step == RegisterSteps.Finish));
        }

        if (solicitation.isBillPayment) {
            const stepSupplier = steps.find(item => item.step == RegisterSteps.SelectSupplier);

            if (stepSupplier) {
                steps.splice(steps.indexOf(stepSupplier), 1);
            }
        }

        steps.forEach((step, index) => {
            if (step.step == RegisterSteps.SelectFlow) {
                step.disabled = !!solicitation.redemptionId;
            }
            else {
                const actualStep = steps.find(item => item.step == currentStep);
                step.disabled = (step.sequence > actualStep.sequence) || loadingRequestSolicitationEdit;
            }

            step.sequence = index;
        });

        setStepItems(steps);

    }, [registerSteps, currentStep]);

    useEffect(() => {
        dispatch(resetDataValidateBarCode());
        dispatch(resetSupplierCreationData());
        dispatch(resetDataUploadFile());
        dispatch(conciergeLoginThunk());
        dispatch(getPointsThunk());
    }, []);

    return (
        <>
            <VertContainer spacingX="regular" spacingY="regular">

                <VertBreadcrumb
                    hasBackButton={true}
                    separator={<VertIcon component={RightOutlined} />}
                    items={[
                        {
                            title: 'Início',
                            path: '/',
                        },
                        {
                            title: 'Solicitação de produtos e serviços',
                            path: '/list-solicitations',
                        },
                        {
                            title: solicitation.redemptionId ? "Editar solicitação" : "Criar nova solicitação",
                            path: '/new-solicitation',
                        }
                    ]}
                />

                <Row className='container-title-main'>
                    <Col span={20}>
                        <Typography.Text className='text-title'>
                            {solicitation.redemptionId ? "Editar solicitação" : "Criar nova solicitação"}
                        </Typography.Text>
                    </Col>
                </Row>

                <div className='container-steps-new-solicitation'>
                    <VertSteps
                        size="small"
                        direction='horizontal'
                        onChange={sequence => {
                            setStepSequence(sequence);
                        }}
                        items={stepItems}
                        initial={0}
                        current={currentSequence}
                    />
                </div>

                {loadingRequestSolicitationEdit
                    ? <VertLoading className='loading-space' />
                    : handleStep(currentStep)
                }

                <ErrorApiModal
                    isOpen={errorRequestSolicitationEdit}
                    onClose={() => navigate("/list-solicitations")}
                />

                <ErrorApiModal
                    isOpen={showModalErrorLogin}
                    onClose={() => navigate("/auth/logout")}
                    subtitle="Será necessário realizar o login novamente!"
                />

            </VertContainer >
        </>
    )
}

export default NewConciergeSolicitationPage;
